import { Action } from 'redux';
import { SessionApi, SessionCreateResponse } from './SessionApi';
import Auth from '../../Auth';

const LOGIN_REQUEST = 'LOGIN_REQUEST';
const LOGIN_SUCCESS = 'LOGIN_SUCCESS';
const LOGIN_FAILURE = 'LOGIN_FAILURE';
const LOGOUT_SUCCESS = 'LOGOUT_SUCCESS';
const SESSION_EXPIRED = 'SESSION_EXPIRED';

type NavigateFunction = (path: string, options?: { replace?: boolean }) => void;

interface SessionAction extends Action {
  message?: string;
  token?: string;
}

export interface SessionReducerState {
  token: string;
  isFetching: boolean;
  error: boolean;
  message: string;
  expired: boolean;
  loggedOut: boolean;
}

const initialState: SessionReducerState = {
  token: '',
  isFetching: false,
  error: false,
  expired: false,
  loggedOut: false,
  message: '',
};

export const sessionReducer = (
  state: SessionReducerState = initialState,
  action: SessionAction
): SessionReducerState => {
  switch (action.type) {
    case LOGIN_REQUEST:
      return { ...state, isFetching: true };
    case LOGIN_SUCCESS:
      return {
        ...state,
        isFetching: false,
        error: false,
        token: action.token!,
        loggedOut: false,
      };
    case LOGIN_FAILURE:
      return {
        ...state,
        isFetching: false,
        error: true,
        message: action.message!,
        loggedOut: false,
      };
    case LOGOUT_SUCCESS:
      return { ...state, loggedOut: true, token: '' };
    case SESSION_EXPIRED:
      return { ...state, expired: true, token: '', loggedOut: false };
    default:
      return state;
  }
};

function requestLogin() {
  return { type: LOGIN_REQUEST };
}

function receiveLogin(token: string) {
  return { type: LOGIN_SUCCESS, token };
}

function loginError(message: string) {
  return { type: LOGIN_FAILURE, message };
}

function receiveLogout() {
  return { type: LOGOUT_SUCCESS };
}

function requestInitialTwoFactorOrSkip(
  dispatch: Function,
  navigate: NavigateFunction,
  redirectUrl: string
) {
  const apiBase = process.env.REACT_APP_API || '';

  return fetch(`${apiBase}/api/graphql`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...Auth.getAuthorizationHeader()    },
    body: JSON.stringify({
      query: `
        mutation {
          requestTwoFactorCode {
            success
            expiresAt
            trusted
            id_token
          }
        }
      `,
    }),
  })
    .then((resp) => resp.json().catch(() => ({} as any)))
    .then((json: any) => {
      if (json && json.errors && json.errors.length) {
        throw new Error(json.errors[0]?.message || 'graphql_error');
      }

      const res = json && json.data && json.data.requestTwoFactorCode;

      if (res && res.trusted) {
        if (res.id_token) {
          Auth.authenticateUser(res.id_token);
          dispatch(receiveLogin(res.id_token));
        }

        navigate(redirectUrl || '/', { replace: true });
        return;
      }

      navigate('/2fa', { replace: true });
    })
    .catch(() => {
      navigate('/2fa', { replace: true });
    });
}

export interface Login {
  (
    username: string,
    password: string,
    navigate: NavigateFunction,
    redirectUrl: string
  ): (dispatch: Function) => void;
}

export const login: Login = (username, password, navigate, redirectUrl) => {
  return async (dispatch: Function) => {
    dispatch(requestLogin());

    try {
      const response: Response = await SessionApi.login(username, password);

      if (!response.ok) {
        dispatch(
          loginError(response.statusText || "The username or password don't match")
        );
        return;
      }

      const json: SessionCreateResponse = await response.json();

      Auth.authenticateUser(json.id_token);
      dispatch(receiveLogin(json.id_token));

      const jwt = Auth.getJWT();

      if (jwt && jwt.enabledAuth === false) {
        navigate(redirectUrl || '/', { replace: true });
        return;
      }

      await requestInitialTwoFactorOrSkip(dispatch, navigate, redirectUrl);
    } catch (error) {
      console.error('Login error:', error);
      dispatch(
        loginError(
          error instanceof Error
            ? error.message
            : "The username or password don't match"
        )
      );
    }
  };
};

export interface ChangeClient {
  (clientId: number, navigate: NavigateFunction, url: string): (
    dispatch: Function
  ) => void;
}

export const changeClient: ChangeClient = (clientId, navigate, url) => {
  return (dispatch: Function) => {
    dispatch(requestLogin());

    return SessionApi.changeClient(clientId)
      .then((resp: SessionCreateResponse & { __typename?: string }) => {
        if (resp && resp.id_token) {
          const { resetState } = require('../../rootReducer');
          dispatch(resetState());

          Auth.deauthenticateUser();
          Auth.authenticateUser(resp.id_token);

          dispatch(receiveLogin(resp.id_token));
          navigate(url);
        } else {
          console.error(
            'changeClient failed: Invalid or missing response',
            resp
          );
          dispatch(
            loginError('Failed to change client — invalid server response.')
          );
        }
      })
      .catch((error: Error) => {
        console.error('changeClient error:', error);
        dispatch(loginError(error.message));
      });
  };
};

export interface ChangeAddon {
  (addonId: number, navigate: NavigateFunction, url: string): (
    dispatch: Function
  ) => void;
}

export const changeAddon: ChangeAddon = (addonId, navigate, url) => {
  return (dispatch: Function) => {
    dispatch(requestLogin());

    return SessionApi.changeAddon(addonId)
      .then((resp: SessionCreateResponse & { __typename?: string }) => {
        if (resp && resp.id_token) {
          const { resetState } = require('../../rootReducer');
          dispatch(resetState());

          Auth.deauthenticateUser();
          Auth.authenticateUser(resp.id_token);

          dispatch(receiveLogin(resp.id_token));
          navigate(url);
        } else {
          console.error(
            'changeAddon failed: Invalid or missing response',
            resp
          );
          dispatch(
            loginError('Failed to change addon — invalid server response.')
          );
        }
      })
      .catch((error: Error) => {
        console.error('changeAddon error:', error);
        dispatch(loginError(error.message));
      });
  };
};

export interface Logout {
  (navigate: NavigateFunction): (dispatch: Function) => void;
}

export const logout: Logout = (navigate) => {
  return (dispatch: Function) => {
    Auth.deauthenticateUser();
    dispatch(receiveLogout());
    navigate('/login');
  };
};

export interface SessionExpired {
  (navigate?: NavigateFunction): (
    dispatch: Function
  ) => SessionAction;
}

export const sessionExpired: SessionExpired = (navigate) => {
  return (dispatch: Function) => {
    if (navigate) {
      navigate('/login');
    }

    return dispatch({ type: SESSION_EXPIRED } as SessionAction);
  };
};